import type { ApiVersion, ServiceVersionInfo } from './meta';

export type DevApiKeyItem = { keyId: string; status: string; createdAt: string };

export function authHeaders(): Record<string, string> {
  const t = localStorage.getItem('afw.dev.token') || '';
  return t ? { 'X-Authorization': `Bearer ${t}` } : {};
}

import { apiFetch } from './client';

export async function devListKeys(): Promise<DevApiKeyItem[]> {
  const res = await apiFetch(`/dev/keys`, { headers: { ...authHeaders() } });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.error?.message || `HTTP ${res.status}`);
  return (j.items || []) as DevApiKeyItem[];
}

export async function devCreateKey(): Promise<{ keyId: string; key: string }>{
  const res = await apiFetch(`/dev/keys`, { method: 'POST', headers: { ...authHeaders() } });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.error?.message || `HTTP ${res.status}`);
  return j as { keyId: string; key: string };
}

export async function devRevokeKey(keyId: string): Promise<void> {
  const res = await apiFetch(`/dev/keys/${keyId}`, { method: 'DELETE', headers: { ...authHeaders() } });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.error?.message || `HTTP ${res.status}`);
}

export async function devGetKeyValue(keyId: string): Promise<{ keyId: string; key: string }>{
  const res = await apiFetch(`/dev/keys/${encodeURIComponent(keyId)}`, { headers: { ...authHeaders() } });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.error?.message || `HTTP ${res.status}`);
  return j as { keyId: string; key: string };
}

export type ServiceInfo = { id: string; name: string; description?: string; versions?: ServiceVersionInfo[] };
export type DevMe = { tenantId: string; name?: string; services: string[]; serviceInfo?: ServiceInfo[]; policy?: { defaultApiVersion?: ApiVersion; serviceVersions?: Record<string, ApiVersion[]>; [key: string]: any }; oauthClientId?: string };
export async function devGetMe(): Promise<DevMe> {
  const res = await apiFetch(`/dev/me`, { headers: { ...authHeaders() } });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.error?.message || `HTTP ${res.status}`);
  return j as DevMe;
}

export type UsageSummary = {
  tenantId: string;
  month: string;
  usedMonth: number;
  perMonthMax?: number;
  remainingMonth?: number;
  perMinuteDefault?: number;
  perService?: Record<string, { perMinute?: number }>;
  byService?: Record<string, number>;
  byKey?: Record<string, number>;
};

export async function devGetUsageSummary(): Promise<UsageSummary> {
  const res = await apiFetch(`/dev/usage`, { headers: { ...authHeaders() } });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.error?.message || `HTTP ${res.status}`);
  return j as UsageSummary;
}

export async function devListUsageEvents(params: { dateFrom?: string; dateTo?: string; service?: string; keyId?: string; limit?: number } = {}): Promise<{ items: Array<{ ts: string; service: string; apiPath?: string; apiKeyId?: string; units?: number; detail?: any }> }>{
  const qs = new URLSearchParams();
  if (params.dateFrom) qs.set('dateFrom', params.dateFrom);
  if (params.dateTo) qs.set('dateTo', params.dateTo);
  if (params.service) qs.set('service', params.service);
  if (params.keyId) qs.set('keyId', params.keyId);
  if (params.limit) qs.set('limit', String(params.limit));
  const res = await apiFetch(`/dev/usage/events${qs.toString() ? '?' + qs.toString() : ''}`, { headers: { ...authHeaders() } });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j?.error?.message || `HTTP ${res.status}`);
  return j as { items: Array<{ ts: string; service: string; apiPath?: string; apiKeyId?: string; units?: number; detail?: any }> };
}
